import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Download, FileSpreadsheet, Archive, BarChart3, Users, Plane, Calendar, HelpCircle, X, AlertCircle, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState('');
  const [showBackupGuide, setShowBackupGuide] = useState(false);
  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.filter(u => u.role === 'bdm' && u.status === 'active'));
    } catch { /* ignore */ }
  };

  const isReminderDay = () => { const d = new Date().getDay(); return d === 5 || d === 1; };
  const showReminder = ['hr', 'accounts'].includes(user?.role) && isReminderDay();

  const buildParams = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (selectedEmployee) params.set('employee_id', selectedEmployee);
    return params.toString() ? `?${params.toString()}` : '';
  };

  const downloadFile = async (url, filename, key) => {
    setLoading(key);
    try {
      const response = await axios.get(`${API}${url}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) { alert('Export failed'); }
    finally { setLoading(''); }
  };

  const qs = buildParams();
  const d = new Date().toISOString().split('T')[0];

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Export Center</h1>
        <p className="text-sm text-gray-500 mb-6">Download expenses, trips, and analytics as Excel or ZIP</p>

        {showReminder && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="backup-reminder">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Reminder: Please backup your expense data for this week.</p>
              <button onClick={() => setShowBackupGuide(true)} className="text-xs text-amber-600 hover:text-amber-800 underline mt-1" data-testid="how-to-backup-btn">How to Backup</button>
            </div>
          </div>
        )}

        {showBackupGuide && (
          <div className="modal-overlay" onClick={() => setShowBackupGuide(false)}>
            <div className="modal-content p-6" onClick={e => e.stopPropagation()} data-testid="backup-guide-modal">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><HelpCircle className="w-5 h-5 text-indigo-500" /><h2 className="text-lg font-bold text-gray-900">How to Backup</h2></div>
                <button onClick={() => setShowBackupGuide(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                {['Set your date range and employee filter above', 'Click "ZIP Export" to download expenses with all bill attachments', 'The ZIP contains an Excel file and a "bills" folder with all receipts', 'Save the ZIP to your company drive for weekly backup'].map((t, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <p>{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Global Filters */}
        <div className="card mb-6" data-testid="export-global-filters">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Filter Exports</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3 inline mr-1" />From Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-field" data-testid="global-start-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3 inline mr-1" />To Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-field" data-testid="global-end-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Users className="w-3 h-3 inline mr-1" />BDM / Employee</label>
              <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)} className="input-field" data-testid="global-employee-filter">
                <option value="">All Employees</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          {(startDate || endDate || selectedEmployee) && (
            <button onClick={() => { setStartDate(''); setEndDate(''); setSelectedEmployee(''); }}
              className="text-xs text-indigo-600 hover:text-indigo-800 mt-3" data-testid="clear-filters-btn">Clear all filters</button>
          )}
        </div>

        {/* Export Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* All Expenses Excel */}
          <div className="card hover:border-indigo-200 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">All Expenses (Excel)</h3>
                <p className="text-xs text-gray-500">With employee summary + category breakdown</p>
              </div>
            </div>
            <button onClick={() => downloadFile(`/export/expenses/excel${qs}`, `expenses_${d}.xlsx`, 'exp')}
              disabled={!!loading} className="btn btn-primary w-full justify-center text-sm" data-testid="export-expenses-excel">
              <Download className="w-4 h-4" />{loading === 'exp' ? 'Generating...' : 'Download'}
            </button>
          </div>

          {/* Person-wise Excel */}
          <div className="card hover:border-indigo-200 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Person-wise Expenses</h3>
                <p className="text-xs text-gray-500">Individual sheet per employee + summary</p>
              </div>
            </div>
            <button onClick={() => downloadFile(`/export/person-wise/excel${qs}`, `person_wise_${d}.xlsx`, 'pw')}
              disabled={!!loading} className="btn btn-primary w-full justify-center text-sm" data-testid="export-person-wise-excel">
              <Download className="w-4 h-4" />{loading === 'pw' ? 'Generating...' : 'Download'}
            </button>
          </div>

          {/* Trips Excel */}
          <div className="card hover:border-indigo-200 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Plane className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">All Trips (Excel)</h3>
                <p className="text-xs text-gray-500">Trip list with advance & reconciliation</p>
              </div>
            </div>
            <button onClick={() => downloadFile(`/export/trips/excel${qs}`, `trips_${d}.xlsx`, 'trip')}
              disabled={!!loading} className="btn btn-primary w-full justify-center text-sm" data-testid="export-trips-excel">
              <Download className="w-4 h-4" />{loading === 'trip' ? 'Generating...' : 'Download'}
            </button>
          </div>

          {/* Analytics Excel */}
          <div className="card hover:border-indigo-200 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Analytics Report</h3>
                <p className="text-xs text-gray-500">Category, payment, monthly trend, trip reconciliation</p>
              </div>
            </div>
            <button onClick={() => downloadFile(`/export/analytics/excel${qs}`, `analytics_${d}.xlsx`, 'analytics')}
              disabled={!!loading} className="btn btn-primary w-full justify-center text-sm" data-testid="export-analytics-excel">
              <Download className="w-4 h-4" />{loading === 'analytics' ? 'Generating...' : 'Download'}
            </button>
          </div>

          {/* ZIP Export */}
          <div className="card hover:border-indigo-200 transition-colors md:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Archive className="w-5 h-5 text-amber-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">ZIP Export (Excel + All Bills)</h3>
                <p className="text-xs text-gray-500">Complete backup — Excel spreadsheet + all bill images/PDFs in a folder</p>
              </div>
            </div>
            <button onClick={() => downloadFile(`/export/expenses/zip${qs}`, `expense_export_${d}.zip`, 'zip')}
              disabled={!!loading} className="btn btn-primary w-full justify-center text-sm" data-testid="export-zip">
              <Archive className="w-4 h-4" />{loading === 'zip' ? 'Generating...' : 'Download ZIP'}
            </button>
          </div>
        </div>

        {/* Admin full exports */}
        {user?.role === 'admin' && (
          <div className="card border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Admin Full Exports</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => downloadFile('/admin/export/expenses/csv?include_deleted=true', 'full_expenses.csv', 'adm-csv')}
                disabled={!!loading} className="btn btn-secondary justify-center text-sm" data-testid="admin-full-csv">
                <Download className="w-4 h-4" />{loading === 'adm-csv' ? 'Exporting...' : 'Full CSV (incl. deleted)'}
              </button>
              <button onClick={() => downloadFile('/admin/export/full/json', 'full_backup.json', 'adm-json')}
                disabled={!!loading} className="btn btn-secondary justify-center text-sm" data-testid="admin-full-json">
                <Download className="w-4 h-4" />{loading === 'adm-json' ? 'Exporting...' : 'Full JSON Backup'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportPage;
